"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { ExternalLinkIcon } from "lucide-react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ToolOutputRendererProps } from "../ui/types";
import {
  type AiArtifactSavedToolOutput,
  buildArtifactDetailPath,
  buildArtifactPreviewPath,
} from "./output";

type Props = ToolOutputRendererProps<AiArtifactSavedToolOutput>;

/** Renders saved artifact title, authenticated detail link, and sandbox preview. */
export function AiArtifactSavedToolOutput({ output, part }: Props) {
  const { i18n } = useLingui();
  const { resolvedTheme } = useTheme();
  const detailPath = buildArtifactDetailPath(
    i18n.locale,
    output.organizationId,
    output.id,
  );
  const previewTheme = resolvedTheme === "dark" ? "dark" : "light";
  const previewPath = buildArtifactPreviewPath(
    output.organizationId,
    output.id,
    i18n.locale,
    previewTheme,
  );
  const isPending =
    part.state === "input-streaming" ||
    part.state === "input-available" ||
    part.state === "approval-requested";

  if (part.state === "output-error") {
    return (
      <div className="overflow-hidden rounded-md border">
        <div className="border-b px-3 py-2">
          <p className="text-destructive text-sm" role="alert">
            {part.errorText ?? (
              <Trans>Artifact save failed. Try again after reloading.</Trans>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="rounded-md border px-3 py-2 text-muted-foreground text-sm">
        <Trans>Saving artifact…</Trans>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate font-medium text-sm">{output.title}</p>
          <Badge variant={output.shared ? "secondary" : "ghost"}>
            {output.shared ? (
              <Trans>Shared with workspace</Trans>
            ) : (
              <Trans>Private artifact</Trans>
            )}
          </Badge>
        </div>
        <Button size="sm" variant="outline" asChild>
          <Link href={detailPath}>
            <ExternalLinkIcon className="size-3.5" />
            <Trans>Open artifact</Trans>
          </Link>
        </Button>
      </div>
      <iframe
        className="h-72 w-full border-0 bg-muted/20"
        referrerPolicy="no-referrer"
        src={previewPath}
        title={output.title}
      />
    </div>
  );
}
